import React from 'react';
import { Button, Form, Image } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { returnUserReadableDateTime } from '../../../../utils/datetime';
import { getDatesField, getPriceField, pricingChoices } from '../utils';

const Request = ({
  requestData = {},
  handleApproveClick,
  handleRejectClick,
  handleCheckBoxClick,
}) => {
  // Prepare values safely
  const priceValidationFields = [
    pricingChoices.VSP,
    pricingChoices.OFFER_PRICE,
  ];
  let priceDifference = 0;
  let priceDifferencePercent = 0;
  let isIncrease = false;

  if (
    priceValidationFields.includes(requestData?.choose) &&
    requestData?.vsp &&
    requestData[getPriceField(requestData)]
  ) {
    const priceField = parseFloat(requestData[getPriceField(requestData)]) || 0;
    const vspField = parseFloat(requestData?.vsp) || 0;
    // Avoid division by zero and perform calculations only when vspField is > 0
    if (vspField > 0) {
      priceDifference = Math.abs(priceField - vspField);
      priceDifferencePercent = (priceDifference / vspField) * 100;
      isIncrease = priceField > vspField;
    }
  }

  const scheduleField = getDatesField(requestData.choose);
  const schedule =
    scheduleField && requestData[scheduleField]
      ? requestData[scheduleField]
      : null;

  const priceTitleKey = getPriceField(requestData) || 'VSP';
  // Ensure we are working on a valid string; remove any extra spaces
  const title =
    priceTitleKey.trim().length > 0
      ? priceTitleKey
          .split('_')
          .map((word) =>
            word
              ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
              : ''
          )
          .join(' ')
      : '';

  // Helper for formatted numeric values
  const formatCurrency = (value) =>
    isNaN(value) ? 'N/A' : `₹${parseFloat(value).toFixed(2)}`;

  return (
    <div
      style={{
        border: requestData?.isSelected ? '1px solid #D60611' : 'none',
        borderRadius: requestData?.isSelected ? '10px' : '0',
        maxWidth: '22.5rem',
      }}
    >
      {/* Group 1: VAN No, Image, and Product Title */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          padding: '1rem',
          backgroundColor: '#FFF',
          marginBottom: '4px',
          gap: '0.5rem',
          borderBottom: '1px solid #E0E0E0',
          borderRadius: '10px',
        }}
      >
        {/* Top Header Row */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingBottom: '5px',
            borderBottom: '2px solid #E0E0E0',
          }}
        >
          <div
            style={{
              color: '#D60611',
              fontWeight: '700',
              fontSize: '14px',
            }}
          >
            VAN No. {requestData?.vanNo || 'N/A'}
          </div>
          <Form.Check
            type="checkbox"
            id={requestData?.objectId}
            checked={!!requestData?.isSelected}
            onChange={handleCheckBoxClick}
          />
        </div>

        {/* Image and Product Title */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '1rem',
          }}
        >
          {/* Product Image */}
          <div
            style={{
              width: '60px',
              height: '60px',
              border: '1px solid #CCC',
              borderRadius: '4px',
              overflow: 'hidden',
            }}
          >
            <Image
              src={requestData?.images || ''}
              alt="product"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
              }}
            />
          </div>

          {/* Product Title */}
          <div style={{ fontSize: '13px', width: '250px' }}>
            <span className="fw-medium">Product Name:</span>{' '}
            {requestData?.productTitle || 'N/A'}
          </div>
        </div>
      </div>

      {/* Group 2: Remaining Details */}
      <div
        style={{
          padding: '1rem',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: '#FFF',
          gap: '0.5rem',
          borderRadius: '10px',
        }}
      >
        <div style={{ display: 'flex' }}>
          <div style={{ width: '52%' }} className="fw-medium">
            CURRENT PRICE:
          </div>
          <div>{formatCurrency(requestData?.vsp)}</div>
        </div>
        <div style={{ display: 'flex' }}>
          <div style={{ width: '52%' }} className="fw-medium">
            New VSP:
          </div>
          <div>{formatCurrency(requestData?.vs_price)}</div>
        </div>
        {title && (
          <div style={{ display: 'flex' }}>
            <div style={{ width: '52%' }} className="fw-medium">
              {title}:
            </div>
            <div>
              {formatCurrency(requestData?.[getPriceField(requestData)])}
            </div>
          </div>
        )}
        {priceValidationFields.includes(requestData?.choose) &&
          requestData?.vsp && (
            <div style={{ display: 'flex' }}>
              <div style={{ width: '52%' }} className="fw-medium">
                DIFFERENCE:
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'flex-start',
                }}
              >
                ₹{isNaN(priceDifference) ? 'N/A' : priceDifference} (
                {isNaN(priceDifferencePercent)
                  ? 'N/A'
                  : priceDifferencePercent.toFixed(2)}
                %) &nbsp;
                <FontAwesomeIcon
                  color={isIncrease ? '#28A745' : '#D60611'}
                  icon={isIncrease ? faSortUp : faSortDown}
                />
              </div>
            </div>
          )}
        <div style={{ display: 'flex' }}>
          <div style={{ width: '52%' }} className="fw-medium">
            REQUESTED DATE:
          </div>
          <div>
            {requestData?.dateSubmitted?.iso
              ? returnUserReadableDateTime(requestData.dateSubmitted.iso)
              : 'N/A'}
          </div>
        </div>
        {schedule && (
          <div style={{ display: 'flex' }}>
            <div style={{ width: '52%' }} className="fw-medium">
              SCHEDULE:
            </div>
            <div>{schedule.desc || 'No schedule details'}</div>
          </div>
        )}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: '1rem',
            backgroundColor: '#FFF',
          }}
        >
          <Button
            style={{
              flex: 1,
              marginRight: '0.5rem',
              borderRadius: '4px',
              fontSize: '14px',
              backgroundColor: '#F4F4F4',
              border: 'none',
              color: '#4A4A4A',
            }}
            id={requestData?.objectId}
            onClick={handleRejectClick}
          >
            Reject
          </Button>
          <Button
            style={{
              flex: 1,
              marginLeft: '0.5rem',
              borderRadius: '4px',
              fontSize: '14px',
              backgroundColor: '#F3D6D7',
              border: 'none',
              color: '#4A4A4A',
            }}
            id={requestData?.objectId}
            onClick={handleApproveClick}
          >
            Approve
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Request;
